import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const initialState = {
  selectedItem: ["dashboard"],
  drawerOpen: false,
  isAddEditFolderVisible: false,
  folders: {},
};

export const fetchFolders = createAsyncThunk(
  "fetchFolders",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get("/folders/tree");
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const addFolder = createAsyncThunk(
  "addFolder",
  async (data, thunkAPI) => {
    try {
      const res = await axios.post("/folders", data);
      return {
        id: res.data.id,
        title: res.data.name,
        icon: res.data.icon,
        parentId: res.data.parentId,
        type: res.data.parentId ? "item" : "collapse",
        url: `/exercise/folders/${res.data.id}`,
        children: [],
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const editFolder = createAsyncThunk(
  "editFolder",
  async (data, thunkAPI) => {
    try {
      console.log(data);
      const res = await axios.patch(`/folders/${data.id}`, {
        name: data.name,
        parentId: data.parentId,
        icon: data.icon,
      });
      return {
        id: res.data.id,
        title: res.data.name,
        icon: res.data.icon,
        parentId: res.data.parentId,
        type: res.data.parentId ? "item" : "collapse",
        url: `/exercise/folders/${res.data.id}`,
        children: [],
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const removeFolder = createAsyncThunk(
  "removeFolder",
  async (id, thunkAPI) => {
    try {
      const res = await axios.delete(`/folders/${id}`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

//update all children with type: "item" and parentId: parentId
const addOrUpdateFolder = (state, folder) => {
  //if folder has parentId => is a child => update the parent's children
  if (folder.parentId) {
    const parentId = folder.parentId;
    const parent = state.folders[parentId];
    if (parent.children.some(({ id }) => id === folder.id)) {
      state.folders[parentId].children = parent.children.map((child) => ({
        ...child,
        ...(child.id === folder.id ? folder : {}),
        type: "item",
        parentId: parentId,
        url: `/exercise/folders/${folder.id}`,
      }));
    } else {
      state.folders[parentId].children = [
        ...parent.children,
        {
          ...folder,
          type: "item",
          parentId: parentId,
          url: `/exercise/folders/${folder.id}`,
        },
      ];
    }
  } else {
    const foundFolder = state.folders[folder.id];
    if (foundFolder) {
      state.folders[folder.id] = {
        ...foundFolder,
        ...folder,
        type: folder.children.length > 0 ? "collapse" : "item",
        children: foundFolder.children.map((child) => ({
          ...child,
          type: "item",
          parentId: folder.id,
          url: `/exercise/folders/${child.id}`,
        })),
        url: `/exercise/folders/${folder.id}`,
      };
    } else {
      state.folders[folder.id] = {
        ...folder,
        type: folder.children.length > 0 ? "collapse" : "item",
        children: folder.children.map((child) => ({
          ...child,
          type: "item",
          parentId: folder.id,
          url: `/exercise/folders/${child.id}`,
        })),
        url: `/exercise/folders/${folder.id}`,
      };
    }
  }
};

const addOrUpdateManyFolders = (state, folders) => {
  folders.forEach((folder) => addOrUpdateFolder(state, folder));
};

const deleteFolder = (state, id) => {
  const foundFolder = state.folders[id];
  if (foundFolder) {
    delete state.folders[id];
  } else {
    const foundSubFolder = Object.values(state.folders)
      .flatMap(({ children }) => children)
      .find((folder) => folder.id === id);
    if (foundSubFolder) {
      const parentFolder = state.folders[foundSubFolder.parentId];
      state.folders[foundSubFolder.parentId] = {
        ...parentFolder,
        children: parentFolder.children.filter((folder) => folder.id !== id),
      };
    }
  }
};

const menu = createSlice({
  name: "menu",
  initialState,
  reducers: {
    toggleAddEditFolder(state, action) {
      state.isAddEditFolderVisible = action.payload;
    },

    activeItem(state, action) {
      state.selectedItem = action.payload;
    },

    openDrawer(state, action) {
      state.drawerOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFolders.fulfilled, (state, action) => {
      addOrUpdateManyFolders(state, action.payload);
    });
    builder.addCase(addFolder.fulfilled, (state, action) => {
      addOrUpdateFolder(state, action.payload);
    });
    builder.addCase(editFolder.fulfilled, (state, action) => {
      const { id, parentId } = action.payload;
      const foundFolder = state.folders[id];
      if (foundFolder) {
        addOrUpdateFolder(state, action.payload);
        return;
      } else {
        const foundSubFolder = Object.values(state.folders)
          .flatMap(({ children }) => children)
          .find((folder) => folder.id === id);
        if (foundSubFolder) {
          if (foundSubFolder.parentId !== parentId) {
            deleteFolder(state, id);
          }
          addOrUpdateFolder(state, action.payload);
          return;
        }
      }
      addOrUpdateFolder(state, action.payload);
    });
    builder.addCase(removeFolder.fulfilled, (state, action) => {
      deleteFolder(state, action.payload);
    });
  },
});

export default menu.reducer;

export const { activeItem, openDrawer, toggleAddEditFolder } = menu.actions;

export const selectFolders = createSelector(
  [(state) => state.menu.folders],
  (folders) => Object.values(folders)
);

export const selectFolder = (id) =>
  createSelector(
    (state) => state.menu.folders,
    (folders) => {
      const foundFolder = folders[id];
      if (!foundFolder) {
        return Object.values(folders)
          .flatMap((folder) => folder.children)
          .find((folder) => folder.id === id);
      } else {
        return foundFolder;
      }
    }
  );
